import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription, map, catchError, filter, of, take, concatMap, from, toArray, tap } from 'rxjs';
import { isNil } from 'lodash';

import { UserPreferencesService } from '@Account/UserPreferences/services/user-preferences.service';
import { TopicsService } from '@Monitor/Core/services/topics.service';

import { userAppPreferencesDefault } from '@Core/Users/data/user-app-preferences-default';
import { MonitoringBackEndDataSourceModel } from '../models/monitoring-back-end-data-source.model';
import { MonitoringSeriesTypeEnum } from '../enums/monitoring-series-type.enum';
import { BrandIdentity } from '@Common/BrandIdentity/models/brand-identity';
import { MonitoringBrand } from '../models/monitoring-brand.model';
import { BrandIdentityService } from '@Common/BrandIdentity/services/brand-identity.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { BriefingImagePropertiesModel } from '@Common/PageBuilder/models/briefing-image-properties.model';
import { BrandCompetitor } from '@api/brandIdentity/models/brand-identity.api.model';
import { MentionSearchSettingService } from '@Common/MonitorShared/services/mention-search-setting.service';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';

@Injectable()
export class MonitoringBrandManagementService implements OnDestroy {
    readonly maxCompetitorLength: number = 11;

    subscriptions: Subscription[] = [];
    defaultColors: string[] = [];
    defaultDataSourceType: MonitoringSeriesTypeEnum = MonitoringSeriesTypeEnum.folder;

    colorList: string[] = [];
    brandList: BrandIdentity[] = [];

    constructor(
        private localizationService: LocalizationService,
        private userPreferencesService: UserPreferencesService,
        private topicsService: TopicsService,
        private brandIdentityService: BrandIdentityService,
        private mentionSearchSettingService: MentionSearchSettingService
    ) {
        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(prefs => prefs.reportsPrefs.defaultChartColors.generalColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors))
            ).subscribe(colors => {
                this.defaultColors = [...Object.values(colors)];
            }),
            this.brandIdentityService.all$.subscribe(brands => {
                this.brandList = brands ?? [];
            }),
            this.mentionSearchSettingService.selectType$.subscribe(type => {
                this.defaultDataSourceType = type;
            })
        );
    }

    getBrandDataSource(brand?: BrandIdentity): Observable<MonitoringBackEndDataSourceModel> {
        const topics: number[] = brand?.topics?.sort((a, b) => Number(b.favorite) - Number(a.favorite))?.map(x => x.topicId) ?? [];
        const source$ = topics.length
            ? this.topicsService.getTopic(topics[0]).pipe(
                filter(x => !!x),
                take(1),
                map(topic => {
                    const dataSource = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.topic, topic.id);
                    dataSource.dataSourceName = topic.name;
                    dataSource.sourceSearchCriteria = topic.searchCriteria;
                    dataSource.filterSearchCriteria.query.dateQuery = dataSource.sourceSearchCriteria.query.dateQuery;
                    return dataSource;
                })
            )
            : of(new MonitoringBackEndDataSourceModel(this.defaultDataSourceType));
        return source$;
    }

    getBrandCompetitors(monitoringBrand: MonitoringBrand): Observable<MonitoringBrand[]> {
        const competitors: MonitoringBrand[] = [];
        const colors: Set<string> = new Set();
        colors.add(monitoringBrand.color);
        const request$ = monitoringBrand.brand?.competitors?.length
            ? from(monitoringBrand.brand.competitors.slice(0, this.maxCompetitorLength)).pipe(
                concatMap((competitor: BrandCompetitor) => {
                    const brand = this.brandList.find(item => !isNil(competitor.id) && item.id === competitor.id) ?? new BrandIdentity();
                    brand.name = competitor.name;
                    return this.addCompetitor(brand, competitors.length, Array.from(colors)).pipe(
                        tap(newCompetitor => {
                            competitors.push(newCompetitor);
                            colors.add(newCompetitor.color);
                        })
                    );
                }),
                toArray()
            )
            : this.addCompetitor(undefined, competitors.length, Array.from(colors)).pipe(
                map(newCompetitor => [newCompetitor])
            );

        return request$;
    }

    addCompetitor(
        brand?: BrandIdentity,
        index: number = 0,
        excludedColors: string[] = []
    ): Observable<MonitoringBrand> {
        const competitor = new MonitoringBrand();

        if (brand?.id) {
            competitor.brand = brand;
        }

        competitor.name = brand?.name || this.localizationService.transform(
            'Briefing.onlineBriefing.settings.competitor.count',
            {count: index + 1}
        );

        competitor.logo = [];
        if (brand?.logo) {
            const logo = new BriefingImagePropertiesModel();
            logo.url = brand.logo.source;
            competitor.logo.push(logo);
        }

        const colors: string[] = [
            ...brand?.colors?.sort((a, b) => Number(b.favorite) - Number(a.favorite))?.map(x => x.color) ?? [],
            ...this.defaultColors
        ].filter(color => !excludedColors.includes(color));
        competitor.color = colors[0];

        return this.getBrandDataSource(brand).pipe(
            take(1),
            map(source => {
                competitor.dataSource = source;
                return competitor;
            })
        );
    }


    /**
     * Competitor cards hide the date picker, so a competitor's data source defaults to "All time".
     * A competitor briefing compares the brand against its competitors over the same window, so the
     * competitor date range must follow the brand's. Call this whenever the brand date range or a
     * competitor source changes to keep them in sync; otherwise the competitor over-time charts send
     * a date facet with null dates and the backend NPEs deriving its DateFilter.
     */
    syncCompetitorDateRangesToBrand(brand: MonitoringBrand, competitors: MonitoringBrand[]): void {
        const brandDateQuery = this.getEffectiveDateQuery(brand?.dataSource);
        if (!brandDateQuery) {
            return;
        }
        competitors?.forEach(competitor => this.applyDateQueryToDataSource(competitor?.dataSource, brandDateQuery));
    }


    getDuplicateBrandIds(brand: MonitoringBrand, competitors: MonitoringBrand[]): Record<number, boolean> {
        const result: Record<number, boolean> = {};
        const ids: number[] = [brand, ...competitors]
            .map(item => item?.brand?.id)
            .filter(id => id !== null && id !== undefined);
        const counts = new Map<number, number>();
        ids.forEach(id => counts.set(id, (counts.get(id) ?? 0) + 1));

        competitors.forEach(competitor => {
            const id = competitor?.brand?.id;
            if (id !== null && id !== undefined && (counts.get(id) ?? 0) > 1) {
                result[id] = true;
            }
        });
        return result;
    }

    private getEffectiveDateQuery(dataSource: MonitoringBackEndDataSourceModel): DateQueryModel {
        return dataSource?.combinedSearchCriteria?.query?.dateQuery
            ?? dataSource?.sourceSearchCriteria?.query?.dateQuery
            ?? dataSource?.filterSearchCriteria?.query?.dateQuery;
    }

    private applyDateQueryToDataSource(dataSource: MonitoringBackEndDataSourceModel, dateQuery: DateQueryModel): void {
        if (!dataSource) {
            return;
        }
        if (dataSource.combinedSearchCriteria?.query) {
            dataSource.combinedSearchCriteria.query.dateQuery = DateQueryModel.clone(dateQuery);
        }
        if (dataSource.combinedSearchCriteria?.filterQuery) {
            dataSource.combinedSearchCriteria.filterQuery.dateQuery = DateQueryModel.clone(dateQuery);
        }
        if (dataSource.sourceSearchCriteria?.query) {
            dataSource.sourceSearchCriteria.query.dateQuery = DateQueryModel.clone(dateQuery);
        }
        if (dataSource.filterSearchCriteria?.query) {
            dataSource.filterSearchCriteria.query.dateQuery = DateQueryModel.clone(dateQuery);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

}
